// Shared UI pieces: brand, market icons, menus, page shell, modal.
import React, { useEffect, useState } from 'react';
import { meta, shortAddr } from './format.js';
import { LANGS, loadLang, t, useLang } from './i18n.js';
import { setTheme, useTheme } from './theme.js';

export const brand = { name: 'Fundex Trading', operator: 'Horizon Digital Architecture LLC', domain: '', logo: 'Fundex' };
// Text next to the logo: its own setting, or the platform name.
export const logoText = () => (brand.logo || brand.name).toUpperCase();
// "Fundex.trading": the brand written with its official domain, used to tell us apart from similarly named businesses.
export const siteName = () => {
  const d = brand.domain;
  if (!d) return brand.name;
  return d.startsWith(brand.name.toLowerCase() + '.') ? brand.name + d.slice(brand.name.length) : brand.name;
};
export function setBrand(cfg) {
  if (cfg && cfg.brandName) brand.name = cfg.brandName;
  if (cfg && cfg.operatorName) brand.operator = cfg.operatorName;
  if (cfg && 'logoName' in cfg) brand.logo = cfg.logoName || '';
  if (cfg && cfg.officialDomain) brand.domain = cfg.officialDomain;
  try { document.title = `${siteName()} · Prop Trading Evaluation`; } catch { /* ignore */ }
}

export function Logo({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <rect x="2" y="14" width="6" height="10" rx="1.5" fill="var(--accent)" />
      <rect x="10" y="8" width="6" height="16" rx="1.5" fill="var(--accent)" opacity="0.7" />
      <rect x="18" y="2" width="6" height="22" rx="1.5" fill="var(--accent)" opacity="0.45" />
    </svg>
  );
}
export const Brand = ({ onClick }) => (
  <a className="brand" href="#/" onClick={onClick} aria-label={t('{brand} home', { brand: brand.name })}><Logo /><span className="brand-name">{logoText()}</span></a>
);

// Logo for any market: coin / company / metal badge, or two overlapping flags for forex.
export function MarketIcon({ symbol, size = 18 }) {
  const m = meta(symbol);
  const [broken, setBroken] = useState(false);
  if (m.category === 'forex' && m.quote) {
    return (
      <span className="fx-icon" style={{ width: size * 1.45, height: size }} aria-hidden="true">
        <img src={`/logos/flag-${m.base}.svg`} width={size} height={size} alt="" loading="lazy" decoding="async" />
        <img src={`/logos/flag-${m.quote}.svg`} width={size} height={size} alt="" loading="lazy" decoding="async" />
      </span>
    );
  }
  if (broken) {
    return <span className="coin-fallback" style={{ width: size, height: size, background: m.color, fontSize: size * 0.42 }} aria-hidden="true">{m.base.slice(0, 2)}</span>;
  }
  return <img className="coin-img" src={`/logos/${m.base}.svg`} width={size} height={size} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} />;
}

export const Chevron = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
);

export const Rule = ({ label, value }) => <div className="rule"><span>{label}</span><b>{value}</b></div>;
export const Stat = ({ label, value }) => <div className="stat"><span className="label">{label}</span><span className="mono">{value}</span></div>;
export function Meter({ value, max, tone }) {
  const w = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return <div className="meter"><div className={'fill ' + tone} style={{ width: w + '%' }} /></div>;
}

export function WalletChip({ user, signOut, go }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);
  return (
    <div className="menu-wrap" onClick={(e) => e.stopPropagation()}>
      <button className="chip" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={t('Wallet menu')}>
        <span className="dot ok" />
        <span className="mono addr">{shortAddr(user.walletAddress)}</span>
      </button>
      {open && (
        <div className="menu right">
          <div className="menu-note mono">{user.walletAddress}</div>
          {go && <button className="menu-item" onClick={() => { setOpen(false); go('leaderboard'); }}>{t('Leaderboard')}</button>}
          {go && <button className="menu-item" onClick={() => { setOpen(false); go('affiliates'); }}>{t('Affiliate program')}</button>}
          {go && <button className="menu-item" onClick={() => { setOpen(false); go('api'); }}>{t('API keys & SDK')}</button>}
          {go && user.isAdmin && <button className="menu-item accent" onClick={() => { setOpen(false); go('admin'); }}>{t('Admin panel')}</button>}
          {go && <button className="menu-item" onClick={() => { setOpen(false); go('terms'); }}>{t('Terms & Risk Disclosure')}</button>}
          <div className="menu-sep" />
          <PrefsMenuItems />
          <div className="menu-sep" />
          <button className="menu-item" onClick={signOut}>{t('Disconnect')}</button>
          <div className="menu-note">{t('Operated by {operator}', { operator: brand.operator })}</div>
        </div>
      )}
    </div>
  );
}

// Header + footer for non-terminal pages.
export function PageShell({ user, signOut, go, current, children, wide }) {
  const links = [
    ['trade', user ? t('Trade') : null], ['leaderboard', t('Leaderboard')], ['affiliates', user ? t('Affiliates') : null], ['admin', user && user.isAdmin ? t('Admin') : null],
  ].filter(([, l]) => l);
  return (
    <div className="hero">
      <div className="hero-grid" aria-hidden="true" />
      <header className="hero-top">
        <Brand onClick={(e) => { e.preventDefault(); go('trade'); }} />
        <nav className="page-nav" aria-label={t('Main')}>
          {links.map(([id, label]) => (
            <button key={id} className={'nav-item' + (current === id ? ' on' : '')} aria-current={current === id ? 'page' : undefined} onClick={() => go(id)}>{label}</button>
          ))}
        </nav>
        <div className="row gap12 hero-right">
          <SitePrefs />
          {user ? <WalletChip user={user} signOut={signOut} go={go} /> : null}
        </div>
      </header>
      <main className={'page' + (wide ? ' wide' : '')}>{children}</main>
      <Footer />
    </div>
  );
}

export const Footer = () => (
  <footer className="site-footer">
    <p className="footer-risk">
      {t('All trading on {brand} is simulated with virtual funds. Evaluation fees are not deposits. Virtual balances have no cash value.', { brand: brand.name })}{' '}
      {t('Selection for the copy-trading program and any rewards are at the sole discretion of {operator} and are not guaranteed.', { operator: brand.operator })}
    </p>
    <p className="footer-risk">
      {t('{site} is owned and operated by {operator}. We are not affiliated with any other company or website using the name “{brand}” or a similar name.', { site: siteName(), operator: brand.operator, brand: brand.logo || brand.name })}
    </p>
    <div className="footer-links">
      <span>© {new Date().getFullYear()} {siteName()}</span>
      <span>{t('Operated by {operator}', { operator: brand.operator })}</span>
      <a href="#/terms">{t('Terms & Risk Disclosure')}</a>
      <a href="#/terms#s6">{t('Identity verification & AML')}</a>
    </div>
  </footer>
);

export function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className={'modal' + (wide ? ' wide' : '')} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label={t('Close')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function useIsMobile() {
  const q = '(max-width: 900px)';
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const f = () => setM(mq.matches);
    mq.addEventListener('change', f);
    return () => mq.removeEventListener('change', f);
  }, []);
  return m;
}

// Language picker + light/dark switch (header of every page and the terminal top bar).
const GlobeIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" /></svg>;
const SunIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>;
const MoonIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" /></svg>;

export function ThemeToggle() {
  const th = useTheme();
  const next = th === 'light' ? 'dark' : 'light';
  return (
    <button className="icon-btn pref-btn" onClick={() => setTheme(next)} aria-label={next === 'light' ? t('Switch to light mode') : t('Switch to dark mode')} title={next === 'light' ? t('Light mode') : t('Dark mode')}>
      {th === 'light' ? <MoonIcon /> : <SunIcon />}
    </button>
  );
}

export function LangPicker({ compact = false }) {
  const l = useLang();
  return (
    <label className={'lang-pick' + (compact ? ' compact' : '')} title={t('Language')}>
      <GlobeIcon />
      <select value={l} onChange={(e) => loadLang(e.target.value, { remember: true })} aria-label={t('Language')}>
        {LANGS.map((x) => <option key={x.code} value={x.code}>{compact ? x.code.toUpperCase() : x.name}</option>)}
      </select>
      {compact && <span className="lang-code">{l.toUpperCase()}</span>}
    </label>
  );
}

export const SitePrefs = () => <div className="site-prefs"><LangPicker compact /><ThemeToggle /></div>;

// Same controls inside the wallet menu (used on phones where the top bar is full).
export function PrefsMenuItems() {
  const th = useTheme();
  const l = useLang();
  return (
    <>
      <label className="menu-item check-item"><GlobeIcon /><span>{t('Language')}</span>
        <select value={l} onChange={(e) => loadLang(e.target.value, { remember: true })} aria-label={t('Language')}>
          {LANGS.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
        </select>
      </label>
      <button className="menu-item" onClick={() => setTheme(th === 'light' ? 'dark' : 'light')}>
        {th === 'light' ? <MoonIcon /> : <SunIcon />}<span>{th === 'light' ? t('Dark mode') : t('Light mode')}</span>
      </button>
    </>
  );
}
